import { forwardRef } from 'react'

import { cn, getShapeClasses } from '../../utils'
import { Icon } from '../Icon'
import { getIconSizeClasses, getPrimaryClasses } from '../Icon/Icon.styles'
import { Label } from '../Label'
import { Text } from '../Text'
import {
    getBaseClasses,
    getNoLabelClasses,
    getSizeClasses,
    getVariantClasses,
} from './Input.styles'
import { InputProps } from './Input.types'

/**
 * @param name - Input name
 * @param label - Input label
 * @param variant - Default: "contained"
 * @param error - If true, renders error state
 * @param color - Default: "primary"
 * @param errorMessage - If true, renders error message
 * @param icon - If provided, renders icon before input
 * @param iconAfter - If provided, renders icon after input
 * @param onChange - Watches for changes in input
 * @param className - If provided, adds classes to button
 * @param disabled - If true, disables button
 * @param type - Default: "input"
 *
 * @example
 *        <Input
 *            error={error}
 *            errorMessage="This is an error"
 *            placeholder="This is a placeholder"
 *            label="Password"
 *            name="password"
 *            type="password"
 *            variant={variant}
 *            color={color}
 *            onChange={() => {}}
 *         />
 * @type
 * InputProps{
 *   extends React.InputHTMLAttributes<HTMLInputElement> {
 *   id?: string
 *   variant: InputVariant
 *   label?: string
 *   name: string
 *   placeholder?: string
 *   type?: string
 *   value?: string
 *   className?: string
 *   color: ColorType
 *   icon?: React.ReactNode
 *   iconAfter?: React.ReactNode
 *   onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
 *   errorMessage?: string
 *   error?: boolean
 *   disabled?: boolean by default it is false
 *  }
 */

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            icon,
            name,
            color,
            label,
            error,
            variant,
            className,
            iconAfter,
            errorMessage,
            inputSize = 'base',
            shape = 'rounded',
            onChange,
            iconClassName,
            leftIconClick,
            rightIconClick,
            disabled = false,
            iconAfterClass,
            iconBeforeClass,
            ...props
        },
        ref
    ) => {
        const classes = cn(
            getShapeClasses(shape),
            getBaseClasses(),
            getVariantClasses(variant, color),
            getSizeClasses(inputSize)
        )
        const iconClasses = cn(
            getPrimaryClasses(color),
            getIconSizeClasses(inputSize),
            iconClassName
        )
        return (
            <div
                className={cn(
                    'flex w-full flex-col',
                    getNoLabelClasses(inputSize, label)
                )}
            >
                {label ? (
                    <Label
                        text={label || ''}
                        variant={variant}
                        color={color}
                        size={inputSize}
                        htmlFor={name}
                    />
                ) : null}
                <div
                    className={cn(
                        'relative',
                        getNoLabelClasses(inputSize, label)
                    )}
                >
                    <input
                        {...props}
                        className={cn(
                            classes,
                            className,
                            icon && !error && 'pl-16',
                            iconAfter &&
                                icon &&
                                !error &&
                                'md:-pr-14 pl-12 pr-10',
                            error ? 'border-[hsl(var(--error-color))]' : ''
                        )}
                        ref={ref}
                        onChange={onChange}
                        name={name}
                        disabled={disabled}
                        value={props.value}
                    />
                    {icon && (
                        <button
                            className={cn(
                                iconClasses,
                                iconBeforeClass,
                                'absolute right-xxl'
                            )}
                            type="button"
                            onClick={leftIconClick}
                        >
                            <Icon
                                className="ml-[5px]"
                                variant={variant}
                                size={inputSize}
                                color={color}
                            >
                                {icon}
                            </Icon>
                        </button>
                    )}
                    {iconAfter && !error && (
                        <button
                            className={cn(
                                iconClasses,
                                iconAfterClass,
                                'absolute left-xxl'
                            )}
                            onClick={rightIconClick}
                            type="button"
                        >
                            <Icon
                                variant={variant}
                                color={color}
                                size={inputSize}
                            >
                                {iconAfter}
                            </Icon>
                        </button>
                    )}
                </div>
                {error && (
                    <Text
                        variant={variant}
                        text={errorMessage || ''}
                        size={inputSize}
                        className="text-2xs font-thin !text-color-error dark:!text-color-error"
                    />
                )}
            </div>
        )
    }
)

Input.displayName = 'Input'
